import { inject } from '@angular/core';
import { AddEntitiesOptions, getEntityType, getIDType, OrArray, UpdateStateCallback } from '@datorama/akita';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { injectProduction } from '@y42/environment';
import { ErrorHandlerService } from '@y42/error-handling';
import { Y42EntityStore } from '@y42/state';
import { Optional } from '@y42/typescript';
import { ensureArray } from '@y42/util/ensure-array';
import { isNotNullish } from '@y42/util/nullish';
import { takePromise } from '@y42/util/take-promise';
import groupBy from 'lodash/groupBy';
import { BehaviorSubject, catchError, debounceTime, filter, map, Subject, switchMap, tap } from 'rxjs';
import { AbstractGitEntityState } from '../../models/abstract-git-entity-state.model';
import { AbstractStoreModelWithChildren, AbstractStoreModelWithParents } from '../../models/abstract-store-model.model';
import { AbstractGitFactoryService } from './abstract-git-factory.service';

type ExtendedGitEntityStoreModel<State extends AbstractGitEntityState> = Optional<
  getEntityType<State> & AbstractStoreModelWithChildren & AbstractStoreModelWithParents,
  '$children' | '$parentFolders' | '$parent'
>;

@UntilDestroy()
export abstract class AbstractGitEntityStore<
  State extends AbstractGitEntityState = AbstractGitEntityState,
  Entity extends getEntityType<State> = getEntityType<State>,
  IDType extends getIDType<State> = getIDType<State>,
> extends Y42EntityStore<State, Entity, IDType> {
  constructor(
    protected readonly cfg: {
      name: string;
      initialState: State;
      gitFactory: AbstractGitFactoryService;
    },
  ) {
    super(cfg.initialState);

    this.counter$$
      .pipe(
        tap((count) => {
          this.resolve$$.next(this.resolve$$.value + count);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  static PENDING_DEBOUNCE = 100;
  static PENDING_TIMEOUT = 3 * 60 * 1000;

  private skipSync = false;
  private readonly resolve$$ = new BehaviorSubject<number>(0);
  private readonly counter$$ = new Subject<number>();
  private readonly production: boolean = injectProduction();
  private readonly errorHandlerService = inject(ErrorHandlerService);

  get pending(): Promise<void> {
    return takePromise(
      this.counter$$.pipe(
        debounceTime(AbstractGitEntityStore.PENDING_DEBOUNCE),
        switchMap(() => this.resolve$$),
        tap((value) => {
          if (value < 0) {
            this.errorHandlerService.error('[AbstractGitEntityStore.pending]: pending counter is < 0.', {
              context: { value },
            });
          }
        }),
        filter((value) => value === 0),
        catchError((error: unknown) => {
          const message = `[AbstractGitEntityStore.pending]: store.pending timeout reached`;
          this.errorHandlerService.error(message, {
            context: { storeName: this.storeName },
          });
          throw new Error(message, {
            cause: error,
          });
        }),
        map(() => undefined),
      ),
      AbstractGitEntityStore.PENDING_TIMEOUT,
    );
  }

  y42PreGitAdd(entity: Entity): Promise<unknown> {
    this.counter$$.next(1);
    return this.cfg.gitFactory
      .build((entity as AbstractStoreModelWithParents).$parentFolders)
      .create(entity)
      .finally(() => {
        this.counter$$.next(-1);
      });
  }

  y42PreGitUpdate(entity: Entity): Promise<unknown> {
    this.counter$$.next(1);
    return this.cfg.gitFactory
      .build((entity as AbstractStoreModelWithParents).$parentFolders)
      .create(entity)
      .finally(() => {
        this.counter$$.next(-1);
      });
  }

  async y42PreGitDelete(entities: Entity[]): Promise<unknown> {
    const existingEntities = entities.filter(Boolean);
    this.counter$$.next(existingEntities.length);
    return Promise.all(
      Object.entries(
        groupBy(existingEntities, (entity) =>
          ((entity as AbstractStoreModelWithParents).$parentFolders ?? []).join('/'),
        ),
      ).map(([parentFolderConcat, part]) => {
        const $parentFolders = parentFolderConcat.split('/');
        return this.cfg.gitFactory.build($parentFolders).delete(part.map((entity) => entity.$name));
      }),
    ).finally(() => {
      this.counter$$.next(-existingEntities.length);
    });
  }

  akitaPreAddEntity = (next: Entity) => {
    if (this.skipSync || !this.y42PreGitAdd) {
      return next;
    }

    console.debug(`[${this.cfg.name}]: y42PreGitAdd`, next);
    this.y42PreGitAdd(next as Entity);
    return next;
  };

  akitaPreUpdateEntity = (_: Entity, next: Entity) => {
    if (this.skipSync || !this.y42PreGitUpdate) {
      return next;
    }

    console.debug(`[${this.cfg.name}]: y42PreGitUpdate`, next);
    this.y42PreGitUpdate(next as Entity);
    return next;
  };

  /**
   * Calling `store.upsert` or similar causes the `akitaPreUpdateEntity` function to be called, which we can disable by setting skipSync to `true`.
   * @param skipSync if the automatic sync with the file system should be skipped
   */
  setSkipSync(skipSync: boolean) {
    this.skipSync = skipSync;
  }

  /**
   * Upsert the given entity without syncing to the file system.
   */
  upsertWithoutSync(id: IDType, entity: Entity) {
    this.withoutSync(() => {
      this.upsert(id, entity);
    });
  }

  /**
   * Upsert the given entities without syncing to the file system.
   */
  upsertManyWithoutSync(entities: Entity[]) {
    this.withoutSync(() => {
      this.upsertMany(entities.filter(Boolean));
    });
  }

  /**
   * Upsert the given entities without syncing to the file system.
   */
  setWithoutSync(entities: Entity[]) {
    this.withoutSync(() => {
      this.set(entities.filter(Boolean));
    });
  }

  updateActiveWithoutSync(newStateOrCallback: UpdateStateCallback<Entity> | Partial<Entity>) {
    this.withoutSync(() => {
      this.updateActive(newStateOrCallback);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  remove(p1: any, options?: { hideWarning: boolean }) {
    const { hideWarning = false } = options || {};
    if (!this.production && !hideWarning) {
      console.warn(
        `[${this.cfg.name}]: store.remove does not automatically sync to git. Did you want to call store.delete?`,
      );
    }

    super.remove(p1);
  }

  delete(ids: OrArray<IDType>) {
    ids = ensureArray(ids);
    if (ids.length === 0) {
      return;
    }

    if (this.skipSync || !this.y42PreGitDelete) {
      console.debug(
        `[${this.cfg.name}]: skipping y42PreGitDelete because of ${
          this.skipSync ? 'skipSync == true' : 'missing y42PreGitDelete'
        }`,
      );

      return super.remove(ids);
    }

    const entitiesToDelete = ids.map((id) => this.getValue().entities![id] as Entity);
    this.y42PreGitDelete(entitiesToDelete);

    super.remove(ids);
  }

  create(
    entity: Omit<ExtendedGitEntityStoreModel<State>, '$path' | '$children' | '$level'>,
    options?: AddEntitiesOptions,
  ) {
    const $path = this.cfg.gitFactory.build(entity.$parentFolders).getPath(entity.$name);

    const enhancedEntity = {
      $parentFolders: entity.$parentFolders || [],
      $children: [],
      $level: 0, // TODO
      ...entity,
      $path,
    } as getEntityType<State>;

    super.add(enhancedEntity as OrArray<Entity>, options);

    return enhancedEntity as getEntityType<State>;
  }

  createMany(
    entities: Array<Omit<ExtendedGitEntityStoreModel<State>, '$path' | '$children'>>,
    options?: AddEntitiesOptions,
  ) {
    if (entities.length === 0) {
      return [];
    }

    const enhancedEntities = entities
      .map((entity) => {
        if (!entity.$parentFolders) {
          return;
        }
        const $parentFolders = entity.$parentFolders;
        const $path = this.cfg.gitFactory.build($parentFolders).getPath(entity.$name);
        const enhancedEntity = {
          $parentFolders,
          $children: [],
          ...entity,
          $path,
        } as Entity;
        return enhancedEntity;
      })
      .filter(isNotNullish);

    super.add(enhancedEntities, options);

    return enhancedEntities;
  }

  private withoutSync(updateFn: () => void): void {
    this.setSkipSync(true);
    updateFn();
    this.setSkipSync(false);
  }
}
